import { useState, useEffect, useCallback, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  superAdminApi,
  type Instance,
  type InstanceDetail,
  type DeleteResult,
  type MachineDetail,
} from '../api/superAdmin';

// System protected environment variables (cannot be edited/deleted)
const PROTECTED_ENV_VARS = ['INSTANCE_SECRET', 'OPENCLAW_STATE_DIR'];

// Map Fly Machine state to DO status for comparison
// Fly uses: started, stopped, suspended, starting, stopping, destroyed
// DO uses: running, stopped, suspended, starting, creating
function isFlyStateMismatch(flyState: string, doStatus: string): boolean {
  const flyToDo: Record<string, string> = {
    started: 'running',
    stopped: 'stopped',
    suspended: 'suspended',
    starting: 'starting',
    stopping: 'stopped',
  };
  const expectedDoStatus = flyToDo[flyState];
  if (!expectedDoStatus) return true; // Unknown fly state
  return expectedDoStatus !== doStatus;
}

export default function InstancesPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [instances, setInstances] = useState<Instance[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [deleteResult, setDeleteResult] = useState<DeleteResult | null>(null);

  // Pagination state
  const [total, setTotal] = useState(0);
  const limit = 20;
  const currentPage = parseInt(searchParams.get('page') || '1', 10);
  const offset = (currentPage - 1) * limit;
  const totalPages = Math.ceil(total / limit);

  // Detail modal state
  const [selectedInstance, setSelectedInstance] = useState<Instance | null>(null);
  const [instanceDetail, setInstanceDetail] = useState<InstanceDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);

  // Tab state
  const [activeTab, setActiveTab] = useState<'info' | 'env'>('info');

  // Environment variables state
  const [envVars, setEnvVars] = useState<Record<string, string>>({});
  const [envLoading, setEnvLoading] = useState(false);
  const [envSaving, setEnvSaving] = useState(false);
  const [envError, setEnvError] = useState<string | null>(null);
  const [editingKey, setEditingKey] = useState<string | null>(null);
  const [editValue, setEditValue] = useState('');
  const [newEnvKey, setNewEnvKey] = useState('');
  const [newEnvValue, setNewEnvValue] = useState('');
  const [showAddForm, setShowAddForm] = useState(false);

  // Action state
  const [actionInProgress, setActionInProgress] = useState<string | null>(null);

  // Machine detail state (for image info)
  const [machineDetail, setMachineDetail] = useState<MachineDetail | null>(null);
  const [machineLoading, setMachineLoading] = useState(false);

  // Image update state
  const [showImageModal, setShowImageModal] = useState(false);
  const [newImageUrl, setNewImageUrl] = useState('');
  const [imageUpdating, setImageUpdating] = useState(false);
  const [imageError, setImageError] = useState<string | null>(null);

  // Sync state
  const [syncing, setSyncing] = useState(false);
  const [syncError, setSyncError] = useState<string | null>(null);
  const [syncSuccess, setSyncSuccess] = useState<string | null>(null);

  // Track if a fetch is in progress to avoid overlapping requests
  const isFetchingRef = useRef(false);

  const fetchInstances = useCallback(async (showLoadingState = true) => {
    // Skip if already fetching (prevent overlapping requests)
    if (isFetchingRef.current) return;

    isFetchingRef.current = true;
    if (showLoadingState) {
      setLoading(true);
    }
    setError(null);

    try {
      const response = await superAdminApi.listInstances({ offset, limit });

      if (!response.success) {
        setError(response.error?.message || 'Failed to fetch instances');
        return;
      }

      setInstances(response.data?.instances || []);
      setTotal(response.data?.total || 0);
    } finally {
      isFetchingRef.current = false;
      setLoading(false);
    }
  }, [offset, limit]);

  // Initial fetch
  useEffect(() => {
    fetchInstances(true);
  }, [fetchInstances]);

  // Auto-refresh every 10 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      fetchInstances(false); // Don't show loading spinner for auto-refresh
    }, 10000);

    return () => clearInterval(interval);
  }, [fetchInstances]);

  const handlePageChange = (page: number) => {
    setSearchParams({ page: String(page) });
  };

  const handleDelete = async (instance: Instance) => {
    if (
      !confirm(
        `Are you sure you want to delete instance "${instance.name}" (${instance.id})?\n\nThis will permanently delete:\n- Fly Machine\n- Fly Volume\n\nThis action cannot be undone.`
      )
    ) {
      return;
    }

    setDeletingId(instance.id);
    setDeleteResult(null);
    setError(null);

    const response = await superAdminApi.deleteInstance(instance.id);
    setDeletingId(null);

    if (!response.success) {
      setError(response.error?.message || 'Failed to delete instance');
      return;
    }

    setDeleteResult(response.data!);
    fetchInstances();
  };

  const handleViewDetail = async (instance: Instance) => {
    setSelectedInstance(instance);
    setDetailLoading(true);
    setInstanceDetail(null);
    setMachineDetail(null);

    const response = await superAdminApi.getInstance(instance.id);
    setDetailLoading(false);

    if (response.success && response.data) {
      setInstanceDetail(response.data);

      // Fetch machine details if machine exists
      if (response.data.do.machineId) {
        setMachineLoading(true);
        const machineResponse = await superAdminApi.getMachine(response.data.do.machineId);
        setMachineLoading(false);
        if (machineResponse.success && machineResponse.data) {
          setMachineDetail(machineResponse.data);
        }
      }
    }
  };

  const handleCloseDetail = () => {
    setSelectedInstance(null);
    setInstanceDetail(null);
    setMachineDetail(null);
    setActiveTab('info');
    setEnvVars({});
    setEnvError(null);
    setEditingKey(null);
    setShowAddForm(false);
    setNewEnvKey('');
    setNewEnvValue('');
    setShowImageModal(false);
    setNewImageUrl('');
    setImageError(null);
    setSyncError(null);
    setSyncSuccess(null);
  };

  const handleSync = async (instanceId: string) => {
    setSyncing(true);
    setSyncError(null);
    setSyncSuccess(null);

    const response = await superAdminApi.syncInstance(instanceId);
    setSyncing(false);

    if (!response.success) {
      setSyncError(response.error?.message || 'Failed to sync instance');
      return;
    }

    setSyncSuccess(response.data?.message || 'Synced successfully');

    // Refresh instance detail
    const detailResponse = await superAdminApi.getInstance(instanceId);
    if (detailResponse.success && detailResponse.data) {
      setInstanceDetail(detailResponse.data);
    }

    // Also refresh the list
    fetchInstances();
  };

  const fetchEnvVars = async (machineId: string) => {
    setEnvLoading(true);
    setEnvError(null);

    const response = await superAdminApi.getMachineEnv(machineId);
    setEnvLoading(false);

    if (!response.success) {
      setEnvError(response.error?.message || 'Failed to fetch environment variables');
      return;
    }

    setEnvVars(response.data?.env || {});
  };

  const handleSaveEnvVar = async (key: string, value: string) => {
    if (!selectedInstance) return;

    setEnvSaving(true);
    setEnvError(null);

    const response = await superAdminApi.updateInstanceEnv(selectedInstance.id, { [key]: value });
    setEnvSaving(false);

    if (!response.success) {
      setEnvError(response.error?.message || 'Failed to update environment variable');
      return;
    }

    // Update local state
    setEnvVars(prev => ({ ...prev, [key]: value }));
    setEditingKey(null);
    setShowAddForm(false);
    setNewEnvKey('');
    setNewEnvValue('');
  };

  const handleDeleteEnvVar = async (key: string) => {
    if (!selectedInstance) return;
    if (!confirm(`Are you sure you want to delete the environment variable "${key}"?`)) return;

    setEnvSaving(true);
    setEnvError(null);

    // Send empty string to delete the variable
    const response = await superAdminApi.updateInstanceEnv(selectedInstance.id, { [key]: '' });
    setEnvSaving(false);

    if (!response.success) {
      setEnvError(response.error?.message || 'Failed to delete environment variable');
      return;
    }

    // Remove from local state
    setEnvVars(prev => {
      const newVars = { ...prev };
      delete newVars[key];
      return newVars;
    });
  };

  const handleAddEnvVar = async () => {
    if (!newEnvKey.trim()) {
      setEnvError('Environment variable key cannot be empty');
      return;
    }
    if (PROTECTED_ENV_VARS.includes(newEnvKey.trim())) {
      setEnvError(`Cannot modify protected variable: ${newEnvKey}`);
      return;
    }
    await handleSaveEnvVar(newEnvKey.trim(), newEnvValue);
  };

  const handleAction = async (
    instanceId: string,
    action: 'start' | 'stop' | 'suspend'
  ) => {
    setActionInProgress(`${instanceId}-${action}`);
    setError(null);

    let response;
    switch (action) {
      case 'start':
        response = await superAdminApi.startInstance(instanceId);
        break;
      case 'stop':
        response = await superAdminApi.stopInstance(instanceId);
        break;
      case 'suspend':
        response = await superAdminApi.suspendInstance(instanceId);
        break;
    }

    setActionInProgress(null);

    if (!response.success) {
      setError(response.error?.message || `Failed to ${action} instance`);
      return;
    }

    // Refresh list after action
    fetchInstances();
  };

  const handleUpdateImage = async () => {
    if (!selectedInstance) return;

    setImageUpdating(true);
    setImageError(null);

    // Pass undefined if empty string to use server's FLY_MACHINE_IMAGE
    const imageToUse = newImageUrl.trim() || undefined;
    const response = await superAdminApi.updateInstanceImage(selectedInstance.id, imageToUse);

    setImageUpdating(false);

    if (!response.success) {
      setImageError(response.error?.message || 'Failed to update image');
      return;
    }

    // Close modal and refresh machine detail
    setShowImageModal(false);
    setNewImageUrl('');

    // Refresh machine detail to show new image
    if (instanceDetail?.do.machineId) {
      setMachineLoading(true);
      const machineResponse = await superAdminApi.getMachine(instanceDetail.do.machineId);
      setMachineLoading(false);
      if (machineResponse.success && machineResponse.data) {
        setMachineDetail(machineResponse.data);
      }
    }
    // Note: No need to refresh instance list - image update doesn't affect list data
  };

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleString();
  };

  const formatTimeAgo = (dateStr: string | null) => {
    if (!dateStr) return 'Never';
    const now = new Date();
    const date = new Date(dateStr);
    const diffMs = now.getTime() - date.getTime();
    const diffSeconds = Math.floor(diffMs / 1000);
    const diffMinutes = Math.floor(diffSeconds / 60);
    const diffHours = Math.floor(diffMinutes / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffSeconds < 60) return 'Just now';
    if (diffMinutes < 60) return `${diffMinutes} min${diffMinutes > 1 ? 's' : ''} ago`;
    if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
    if (diffDays < 7) return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
    return formatDate(dateStr);
  };

  const formatTimestamp = (ts: number | null) => {
    if (!ts) return 'Never';
    return new Date(ts).toLocaleString();
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'running':
        return 'text-green-400';
      case 'starting':
        return 'text-yellow-400';
      case 'suspended':
        return 'text-blue-400';
      case 'stopped':
        return 'text-gray-400';
      default:
        return 'text-gray-400';
    }
  };

  const getAvailableActions = (status: string) => {
    switch (status) {
      case 'running':
        return ['stop', 'suspend'] as const;
      case 'suspended':
        return ['start', 'stop'] as const;
      case 'stopped':
        return ['start'] as const;
      case 'creating':
      case 'starting':
        // Allow admin to force stop stuck instances
        return ['stop'] as const;
      default:
        return [] as const;
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold text-white">Instance Management</h2>
        <button
          onClick={() => fetchInstances(true)}
          disabled={loading}
          className="px-4 py-2 bg-gray-700 text-white rounded hover:bg-gray-600 transition disabled:opacity-50"
        >
          {loading ? 'Loading...' : 'Refresh'}
        </button>
      </div>

      {error && (
        <div className="bg-red-500/10 border border-red-500 text-red-500 px-4 py-3 rounded">
          {error}
        </div>
      )}

      {deleteResult && (
        <div
          className={`px-4 py-3 rounded ${
            deleteResult.success
              ? 'bg-green-500/10 border border-green-500 text-green-500'
              : 'bg-yellow-500/10 border border-yellow-500 text-yellow-500'
          }`}
        >
          <p className="font-medium mb-2">
            {deleteResult.success ? 'Delete Successful' : 'Delete Partial'}
          </p>
          {deleteResult.deleted.length > 0 && (
            <div className="text-sm">
              <p className="mb-1">Deleted resources:</p>
              <ul className="list-disc list-inside">
                {deleteResult.deleted.map((r, i) => (
                  <li key={i}>{r}</li>
                ))}
              </ul>
            </div>
          )}
          {deleteResult.errors && deleteResult.errors.length > 0 && (
            <div className="text-sm mt-2 text-red-400">
              <p className="mb-1">Errors:</p>
              <ul className="list-disc list-inside">
                {deleteResult.errors.map((e, i) => (
                  <li key={i}>{e}</li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      {/* Instance List */}
      <div className="bg-gray-800 rounded-lg border border-gray-700 overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-700 flex justify-between items-center">
          <h3 className="text-lg font-semibold text-white">
            Instances ({total})
          </h3>
          {totalPages > 1 && (
            <div className="text-sm text-gray-400">
              Page {currentPage} of {totalPages}
            </div>
          )}
        </div>

        {loading ? (
          <div className="px-6 py-8 text-center text-gray-400">
            Loading instances...
          </div>
        ) : instances.length === 0 ? (
          <div className="px-6 py-8 text-center text-gray-400">
            No instances found.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-900">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase">
                    Name
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase">
                    Owner ID
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase">
                    Status
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase">
                    Machine ID
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase">
                    Last Activity
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase">
                    Created
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-700">
                {instances.map((instance) => (
                  <tr key={instance.id} className="hover:bg-gray-700/50">
                    <td className="px-4 py-3 text-sm text-white">
                      <button
                        onClick={() => handleViewDetail(instance)}
                        className="hover:text-blue-400 hover:underline"
                      >
                        {instance.name}
                      </button>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-300 font-mono">
                      <span title={instance.owner_id}>
                        {instance.owner_id.slice(0, 8)}...
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm">
                      <span className={getStatusColor(instance.status)}>
                        {instance.status}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-300 font-mono">
                      {instance.fly_machine_id ? (
                        <span title={instance.fly_machine_id}>
                          {instance.fly_machine_id.slice(0, 12)}...
                        </span>
                      ) : (
                        <span className="text-gray-500">-</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-300">
                      {instance.last_activity_at ? (
                        <span title={formatDate(instance.last_activity_at)}>
                          {formatTimeAgo(instance.last_activity_at)}
                        </span>
                      ) : (
                        <span className="text-gray-500">Never</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-400">
                      {formatDate(instance.created_at)}
                    </td>
                    <td className="px-4 py-3 text-sm">
                      <div className="flex gap-2">
                        {getAvailableActions(instance.status).map((action) => (
                          <button
                            key={action}
                            onClick={() => handleAction(instance.id, action)}
                            disabled={actionInProgress === `${instance.id}-${action}`}
                            className={`px-2 py-1 text-xs font-medium rounded transition disabled:opacity-50 ${
                              action === 'start'
                                ? 'bg-green-600 hover:bg-green-700 text-white'
                                : action === 'stop'
                                ? 'bg-yellow-600 hover:bg-yellow-700 text-white'
                                : 'bg-blue-600 hover:bg-blue-700 text-white'
                            }`}
                          >
                            {actionInProgress === `${instance.id}-${action}`
                              ? '...'
                              : action.charAt(0).toUpperCase() + action.slice(1)}
                          </button>
                        ))}
                        <button
                          onClick={() => handleDelete(instance)}
                          disabled={deletingId === instance.id}
                          className="px-2 py-1 bg-red-600 text-white text-xs font-medium rounded hover:bg-red-700 transition disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {deletingId === instance.id ? '...' : 'Delete'}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="px-6 py-4 border-t border-gray-700 flex justify-center gap-2">
            <button
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage === 1}
              className="px-3 py-1 bg-gray-700 text-white text-sm rounded hover:bg-gray-600 transition disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Previous
            </button>
            {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
              let page: number;
              if (totalPages <= 5) {
                page = i + 1;
              } else if (currentPage <= 3) {
                page = i + 1;
              } else if (currentPage >= totalPages - 2) {
                page = totalPages - 4 + i;
              } else {
                page = currentPage - 2 + i;
              }
              return (
                <button
                  key={page}
                  onClick={() => handlePageChange(page)}
                  className={`px-3 py-1 text-sm rounded transition ${
                    page === currentPage
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-700 text-white hover:bg-gray-600'
                  }`}
                >
                  {page}
                </button>
              );
            })}
            <button
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage === totalPages}
              className="px-3 py-1 bg-gray-700 text-white text-sm rounded hover:bg-gray-600 transition disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Next
            </button>
          </div>
        )}
      </div>

      {/* Instance Detail Modal */}
      {selectedInstance && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-gray-800 rounded-lg border border-gray-700 w-full max-w-2xl mx-4 max-h-[90vh] flex flex-col">
            <div className="px-6 py-4 border-b border-gray-700 flex justify-between items-center">
              <div>
                <h3 className="text-lg font-semibold text-white">
                  Instance Detail - {selectedInstance.name}
                </h3>
                <p className="text-xs text-gray-400 font-mono mt-1">
                  {selectedInstance.id}
                </p>
              </div>
              <div className="flex items-center gap-2">
                {instanceDetail && !instanceDetail.isSynced && (
                  <button
                    onClick={() => handleSync(selectedInstance.id)}
                    disabled={syncing}
                    className="px-3 py-1.5 bg-yellow-600 text-white text-sm font-medium rounded hover:bg-yellow-700 transition disabled:opacity-50"
                  >
                    {syncing ? 'Syncing...' : 'Sync to DB'}
                  </button>
                )}
                <button
                  onClick={handleCloseDetail}
                  className="text-gray-400 hover:text-white text-2xl leading-none"
                >
                  &times;
                </button>
              </div>
            </div>

            {/* Tabs */}
            <div className="px-6 pt-4 border-b border-gray-700">
              <div className="flex gap-4">
                <button
                  onClick={() => setActiveTab('info')}
                  className={`pb-3 px-1 text-sm font-medium border-b-2 transition ${
                    activeTab === 'info'
                      ? 'border-blue-500 text-blue-400'
                      : 'border-transparent text-gray-400 hover:text-white'
                  }`}
                >
                  Basic Info
                </button>
                <button
                  onClick={() => {
                    setActiveTab('env');
                    if (instanceDetail?.do.machineId && Object.keys(envVars).length === 0 && !envLoading) {
                      fetchEnvVars(instanceDetail.do.machineId);
                    }
                  }}
                  className={`pb-3 px-1 text-sm font-medium border-b-2 transition ${
                    activeTab === 'env'
                      ? 'border-blue-500 text-blue-400'
                      : 'border-transparent text-gray-400 hover:text-white'
                  }`}
                >
                  Environment Variables
                </button>
              </div>
            </div>

            <div className="px-6 py-4 space-y-4 overflow-y-auto flex-1">
              {/* Sync messages */}
              {syncError && (
                <div className="bg-red-500/10 border border-red-500 text-red-500 px-3 py-2 rounded text-sm">
                  {syncError}
                </div>
              )}
              {syncSuccess && (
                <div className="bg-green-500/10 border border-green-500 text-green-500 px-3 py-2 rounded text-sm">
                  {syncSuccess}
                </div>
              )}

              {/* Status mismatch warning */}
              {instanceDetail && !instanceDetail.isSynced && (
                <div className="bg-yellow-500/10 border border-yellow-500 text-yellow-500 px-4 py-3 rounded">
                  <p className="font-medium">Status mismatch detected</p>
                  <p className="text-sm mt-1">
                    DO (live) and DB (cached) data are out of sync. Click "Sync to DB" to fix.
                  </p>
                </div>
              )}

              {activeTab === 'info' && (
                <>
                  {detailLoading ? (
                    <div className="text-center text-gray-400 py-8">
                      Loading real-time data from DO...
                    </div>
                  ) : instanceDetail ? (
                    <>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <div className="text-xs text-gray-400 uppercase mb-1">Name</div>
                          <div className="text-white">{instanceDetail.name}</div>
                        </div>
                        <div>
                          <div className="text-xs text-gray-400 uppercase mb-1">DO Status</div>
                          <div className={getStatusColor(instanceDetail.do.status)}>
                            {instanceDetail.do.status}
                          </div>
                        </div>
                        <div>
                          <div className="text-xs text-gray-400 uppercase mb-1">Real Status (Fly)</div>
                          {machineLoading ? (
                            <div className="text-gray-500 text-sm">Loading...</div>
                          ) : machineDetail ? (
                            <div className="flex items-center gap-2">
                              <span className={getStatusColor(machineDetail.state)}>
                                {machineDetail.state}
                              </span>
                              {isFlyStateMismatch(machineDetail.state, instanceDetail.do.status) && (
                                <span className="text-yellow-500 text-xs" title="Status mismatch between DO and Fly">
                                  (mismatch)
                                </span>
                              )}
                            </div>
                          ) : (
                            <div className="text-gray-500">-</div>
                          )}
                        </div>
                        <div>
                          <div className="text-xs text-gray-400 uppercase mb-1">Instance ID</div>
                          <div className="text-white font-mono text-sm break-all">
                            {instanceDetail.instanceId}
                          </div>
                        </div>
                        <div>
                          <div className="text-xs text-gray-400 uppercase mb-1">Owner ID</div>
                          <div className="text-white font-mono text-sm break-all">
                            {instanceDetail.ownerId}
                          </div>
                        </div>
                        <div>
                          <div className="text-xs text-gray-400 uppercase mb-1">Machine ID</div>
                          <div className="text-white font-mono text-sm">
                            {instanceDetail.do.machineId || '-'}
                          </div>
                        </div>
                        <div>
                          <div className="text-xs text-gray-400 uppercase mb-1">Volume ID</div>
                          <div className="text-white font-mono text-sm">
                            {instanceDetail.do.volumeId || '-'}
                          </div>
                        </div>
                      </div>
                      <div className="border-t border-gray-700 pt-4">
                        <div className="text-xs text-gray-400 uppercase mb-2">
                          Real-time Data (from DO)
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <div className="text-xs text-gray-500 mb-1">Last Activity</div>
                            <div className="text-green-400 font-medium">
                              {formatTimestamp(instanceDetail.do.lastActivityAt)}
                            </div>
                          </div>
                          <div>
                            <div className="text-xs text-gray-500 mb-1">Last Started</div>
                            <div className="text-white">
                              {formatTimestamp(instanceDetail.do.lastStartedAt)}
                            </div>
                          </div>
                        </div>
                      </div>
                      <div className="border-t border-gray-700 pt-4">
                        <div className="text-xs text-gray-500 mb-1">Created</div>
                        <div className="text-white">{formatDate(instanceDetail.createdAt)}</div>
                      </div>

                      {/* Machine Image Section */}
                      <div className="border-t border-gray-700 pt-4">
                        <div className="flex justify-between items-center mb-2">
                          <div className="text-xs text-gray-400 uppercase">Machine Image</div>
                          {instanceDetail.do.machineId && (
                            <button
                              onClick={() => setShowImageModal(true)}
                              disabled={machineLoading || imageUpdating}
                              className="px-3 py-1 bg-blue-600 text-white text-xs font-medium rounded hover:bg-blue-700 transition disabled:opacity-50"
                            >
                              Update
                            </button>
                          )}
                        </div>
                        {machineLoading ? (
                          <div className="text-gray-400 text-sm">Loading machine details...</div>
                        ) : machineDetail ? (
                          <div className="space-y-2">
                            <div className="bg-gray-900 rounded p-3 font-mono text-sm text-green-400 break-all">
                              {machineDetail.config.image}
                            </div>
                            {machineDetail.image_ref && (
                              <div className="grid grid-cols-3 gap-2 text-sm">
                                <div>
                                  <div className="text-xs text-gray-500">Registry</div>
                                  <div className="text-gray-300">{machineDetail.image_ref.registry}</div>
                                </div>
                                <div>
                                  <div className="text-xs text-gray-500">Repository</div>
                                  <div className="text-gray-300">{machineDetail.image_ref.repository}</div>
                                </div>
                                <div>
                                  <div className="text-xs text-gray-500">Tag</div>
                                  <div className="text-gray-300">{machineDetail.image_ref.tag}</div>
                                </div>
                              </div>
                            )}
                          </div>
                        ) : instanceDetail.do.machineId ? (
                          <div className="text-gray-500 text-sm">Failed to load machine details</div>
                        ) : (
                          <div className="text-gray-500 text-sm">No machine associated</div>
                        )}
                      </div>
                    </>
                  ) : (
                    <div className="text-center text-gray-400 py-8">
                      Failed to load instance detail
                    </div>
                  )}
                </>
              )}

              {activeTab === 'env' && (
                <>
                  {!instanceDetail?.do.machineId ? (
                    <div className="text-center text-gray-400 py-8">
                      No machine associated with this instance
                    </div>
                  ) : envLoading ? (
                    <div className="text-center text-gray-400 py-8">
                      Loading environment variables...
                    </div>
                  ) : (
                    <>
                      {envError && (
                        <div className="bg-red-500/10 border border-red-500 text-red-500 px-4 py-2 rounded text-sm">
                          {envError}
                        </div>
                      )}

                      {/* Add new variable button */}
                      <div className="flex justify-between items-center">
                        <div className="text-xs text-gray-400 uppercase">Environment Variables</div>
                        <button
                          onClick={() => setShowAddForm(true)}
                          disabled={showAddForm || envSaving}
                          className="px-3 py-1 bg-blue-600 text-white text-xs font-medium rounded hover:bg-blue-700 transition disabled:opacity-50"
                        >
                          + Add
                        </button>
                      </div>

                      {/* Add form */}
                      {showAddForm && (
                        <div className="bg-gray-700/50 rounded p-3 space-y-2">
                          <input
                            type="text"
                            placeholder="KEY"
                            value={newEnvKey}
                            onChange={(e) => setNewEnvKey(e.target.value.toUpperCase())}
                            className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded text-white text-sm font-mono"
                          />
                          <input
                            type="text"
                            placeholder="value"
                            value={newEnvValue}
                            onChange={(e) => setNewEnvValue(e.target.value)}
                            className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded text-white text-sm"
                          />
                          <div className="flex gap-2 justify-end">
                            <button
                              onClick={() => {
                                setShowAddForm(false);
                                setNewEnvKey('');
                                setNewEnvValue('');
                                setEnvError(null);
                              }}
                              className="px-3 py-1 bg-gray-600 text-white text-xs rounded hover:bg-gray-500"
                            >
                              Cancel
                            </button>
                            <button
                              onClick={handleAddEnvVar}
                              disabled={envSaving || !newEnvKey.trim()}
                              className="px-3 py-1 bg-green-600 text-white text-xs rounded hover:bg-green-700 disabled:opacity-50"
                            >
                              {envSaving ? 'Saving...' : 'Save'}
                            </button>
                          </div>
                        </div>
                      )}

                      {/* Variables table */}
                      <div className="border border-gray-700 rounded overflow-hidden">
                        <table className="w-full table-fixed">
                          <thead className="bg-gray-900">
                            <tr>
                              <th className="px-3 py-2 text-left text-xs font-medium text-gray-400 uppercase w-2/5">Key</th>
                              <th className="px-3 py-2 text-left text-xs font-medium text-gray-400 uppercase">Value</th>
                              <th className="px-3 py-2 text-right text-xs font-medium text-gray-400 uppercase w-28">Actions</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-gray-700">
                            {Object.entries(envVars).length === 0 ? (
                              <tr>
                                <td colSpan={3} className="px-3 py-4 text-center text-gray-500 text-sm">
                                  No environment variables
                                </td>
                              </tr>
                            ) : (
                              Object.entries(envVars)
                                .sort(([a], [b]) => a.localeCompare(b))
                                .map(([key, value]) => {
                                  const isProtected = PROTECTED_ENV_VARS.includes(key);
                                  const isEditing = editingKey === key;

                                  return (
                                    <tr key={key} className="hover:bg-gray-700/50">
                                      <td className="px-3 py-2 text-sm font-mono text-white truncate" title={key}>
                                        <span className="break-all">{key}</span>
                                        {isProtected && (
                                          <span className="ml-2 text-yellow-500" title="Protected variable">
                                            🔒
                                          </span>
                                        )}
                                      </td>
                                      <td className="px-3 py-2 text-sm min-w-0">
                                        {isEditing ? (
                                          <input
                                            type="text"
                                            value={editValue}
                                            onChange={(e) => setEditValue(e.target.value)}
                                            className="w-full px-2 py-1 bg-gray-700 border border-gray-600 rounded text-white text-sm"
                                            autoFocus
                                          />
                                        ) : (
                                          <span className={`font-mono break-all ${isProtected ? 'text-gray-500' : 'text-gray-300'}`}>
                                            {isProtected ? '••••••••' : value}
                                          </span>
                                        )}
                                      </td>
                                      <td className="px-3 py-2 text-right whitespace-nowrap">
                                        {isProtected ? (
                                          <span className="text-xs text-gray-500">Protected</span>
                                        ) : isEditing ? (
                                          <div className="flex gap-1 justify-end">
                                            <button
                                              onClick={() => setEditingKey(null)}
                                              className="px-2 py-1 text-xs text-gray-400 hover:text-white"
                                            >
                                              Cancel
                                            </button>
                                            <button
                                              onClick={() => handleSaveEnvVar(key, editValue)}
                                              disabled={envSaving}
                                              className="px-2 py-1 text-xs bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-50"
                                            >
                                              {envSaving ? '...' : 'Save'}
                                            </button>
                                          </div>
                                        ) : (
                                          <div className="flex gap-1 justify-end">
                                            <button
                                              onClick={() => {
                                                setEditingKey(key);
                                                setEditValue(value);
                                              }}
                                              className="px-2 py-1 text-xs text-blue-400 hover:text-blue-300"
                                              title="Edit"
                                            >
                                              Edit
                                            </button>
                                            <button
                                              onClick={() => handleDeleteEnvVar(key)}
                                              disabled={envSaving}
                                              className="px-2 py-1 text-xs text-red-400 hover:text-red-300 disabled:opacity-50"
                                              title="Delete"
                                            >
                                              Delete
                                            </button>
                                          </div>
                                        )}
                                      </td>
                                    </tr>
                                  );
                                })
                            )}
                          </tbody>
                        </table>
                      </div>

                      <div className="text-xs text-gray-500">
                        🔒 Protected variables cannot be modified or deleted
                      </div>
                    </>
                  )}
                </>
              )}
            </div>

            <div className="px-6 py-4 border-t border-gray-700 flex justify-end">
              <button
                onClick={handleCloseDetail}
                className="px-4 py-2 bg-gray-700 text-white rounded hover:bg-gray-600 transition"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Image Update Modal */}
      {showImageModal && selectedInstance && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-gray-800 rounded-lg border border-gray-700 w-full max-w-md mx-4">
            <div className="px-6 py-4 border-b border-gray-700">
              <h3 className="text-lg font-semibold text-white">Update Machine Image</h3>
            </div>
            <div className="px-6 py-4 space-y-4">
              {imageError && (
                <div className="bg-red-500/10 border border-red-500 text-red-500 px-4 py-2 rounded text-sm">
                  {imageError}
                </div>
              )}
              <div>
                <label className="block text-sm text-gray-400 mb-2">
                  New Image (leave empty to use server config):
                </label>
                <input
                  type="text"
                  value={newImageUrl}
                  onChange={(e) => setNewImageUrl(e.target.value)}
                  placeholder="registry.fly.io/openclaw-machines:latest"
                  className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded text-white text-sm font-mono"
                  disabled={imageUpdating}
                />
              </div>
              <div className="bg-yellow-500/10 border border-yellow-500 rounded p-3 text-yellow-500 text-sm">
                Machine will restart with new image
              </div>
            </div>
            <div className="px-6 py-4 border-t border-gray-700 flex justify-end gap-3">
              <button
                onClick={() => {
                  setShowImageModal(false);
                  setNewImageUrl('');
                  setImageError(null);
                }}
                disabled={imageUpdating}
                className="px-4 py-2 bg-gray-700 text-white rounded hover:bg-gray-600 transition disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleUpdateImage}
                disabled={imageUpdating}
                className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition disabled:opacity-50"
              >
                {imageUpdating ? 'Updating...' : 'Update & Restart'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Help Section */}
      <div className="bg-gray-800/50 rounded-lg p-6 border border-gray-700">
        <h3 className="text-lg font-semibold text-white mb-4">
          Instance ID Reference
        </h3>
        <div className="space-y-3 text-sm text-gray-400">
          <p>
            <strong className="text-white">From Fly Machine name:</strong>{' '}
            Machine names follow the pattern{' '}
            <code className="text-green-400">openclaw_XXXXXXXX</code> where
            XXXXXXXX is the first 8 characters of the instance ID.
          </p>
          <p>
            <strong className="text-white">From Supabase:</strong> The instance
            ID is the user's UUID from Supabase Auth.
          </p>
        </div>
      </div>
    </div>
  );
}
